import { FormEvent, useEffect, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  adminListEcosystems,
  adminCreateEcosystem,
  adminUpdateEcosystem,
  adminDecommissionEcosystem,
  adminReactivateEcosystem,
  adminListEcosystemGroupMembers,
  adminAddEcosystemGroupMember,
  adminRemoveEcosystemGroupMember,
} from '@frontend/api-clients/ecosystemsClient';
import type { EcosystemAdminEntry, EcosystemGroupMember, User } from '@frontend/types';

interface EcosystemAdminPageProps {
  user: User;
}

const ALL_SERVICES = ['gitea', 'nextcloud', 'openproject', 'woodpecker'];
const PALETTE_KEYS = ['panelStart', 'panelEnd', 'border', 'serviceStart', 'serviceEnd', 'serviceBorder'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defaultPalette(): Record<string, string> {
  return {
    panelStart: '#1a2a3a',
    panelEnd: '#0f1923',
    border: '#2d4a6a',
    serviceStart: '#1c3a5a',
    serviceEnd: '#0f2236',
    serviceBorder: '#2d5a8a',
  };
}

function buildProvisionCommands(ecosystemId: string): string {
  return `# Run from the infrastructure/ directory on the host
ECOSYSTEM_ID="${ecosystemId}"

# 1 – Create Keycloak realm + clients
bash scripts/provision-keycloak-realm.sh "$ECOSYSTEM_ID"

# 2 – Create Gitea organisation
bash scripts/provision-gitea-org.sh "$ECOSYSTEM_ID"

# 3 – Create Nextcloud group + team space
bash scripts/provision-nextcloud.sh "$ECOSYSTEM_ID"

# 4 – Seed OpenProject instance
bash scripts/provision-openproject.sh "$ECOSYSTEM_ID"

# 5 – Restart portal to pick up registry changes (optional if hot-reload fired)
docker compose -f portal/docker-compose.yml restart portal`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ServiceCheckboxes({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (services: string[]) => void;
}) {
  function toggle(svc: string) {
    if (selected.includes(svc)) {
      onChange(selected.filter((s) => s !== svc));
    } else {
      onChange([...selected, svc]);
    }
  }
  return (
    <div className="eco-admin-service-checks">
      {ALL_SERVICES.map((svc) => (
        <label key={svc} className="eco-admin-checkbox-label">
          <input
            type="checkbox"
            checked={selected.includes(svc)}
            onChange={() => toggle(svc)}
          />
          {svc}
        </label>
      ))}
    </div>
  );
}

function PaletteEditor({
  palette,
  onChange,
}: {
  palette: Record<string, string>;
  onChange: (palette: Record<string, string>) => void;
}) {
  function update(key: string, value: string) {
    onChange({ ...palette, [key]: value });
  }
  return (
    <div className="eco-admin-palette-grid">
      {PALETTE_KEYS.map((key) => (
        <div key={key} className="eco-admin-palette-row">
          <label className="eco-admin-label">{key}</label>
          <div className="eco-admin-palette-input-group">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(palette[key] || '') ? palette[key] : '#000000'}
              onChange={(e) => update(key, e.target.value)}
              className="eco-admin-color-swatch"
            />
            <input
              type="text"
              value={palette[key] || ''}
              onChange={(e) => update(key, e.target.value)}
              placeholder="#rrggbb or rgba(…)"
              className="eco-admin-input"
              style={{ flexGrow: 1 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Ecosystem Form
// ---------------------------------------------------------------------------

interface AddFormState {
  id: string;
  displayName: string;
  memberGroup: string;
  giteaOrg: string;
  logo: string;
  services: string[];
  palette: Record<string, string>;
}

function AddEcosystemForm({
  onCreated,
  onCancel,
}: {
  onCreated: (entry: EcosystemAdminEntry) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<AddFormState>({
    id: '',
    displayName: '',
    memberGroup: '',
    giteaOrg: '',
    logo: '',
    services: [...ALL_SERVICES],
    palette: defaultPalette(),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof AddFormState>(key: K, value: AddFormState[K]) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Auto-fill derived fields only when the user hasn't touched them yet
      if (key === 'id') {
        const id = value as string;
        if (!prev.memberGroup || prev.memberGroup === `${prev.id}-users`) {
          next.memberGroup = id ? `${id}-users` : '';
        }
        if (!prev.giteaOrg || prev.giteaOrg === prev.id) {
          next.giteaOrg = id;
        }
        if (!prev.displayName || prev.displayName === prev.id) {
          next.displayName = id;
        }
      }
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      const created = await adminCreateEcosystem(token, {
        id: form.id,
        displayName: form.displayName,
        logo: form.logo || undefined,
        memberGroup: form.memberGroup || undefined,
        giteaOrg: form.giteaOrg || undefined,
        services: form.services,
        palette: form.palette,
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ecosystem');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="eco-admin-panel" onSubmit={handleSubmit}>
      <h3 className="eco-admin-panel-title">New Ecosystem</h3>

      {error && <div className="settings-message settings-error">{error}</div>}

      <div className="eco-admin-form-grid">
        <div className="eco-admin-field">
          <label className="eco-admin-label" htmlFor="new-id">Ecosystem ID *</label>
          <input
            id="new-id"
            className="eco-admin-input"
            type="text"
            required
            pattern="^[a-z][a-z0-9-]{1,48}[a-z0-9]$"
            title="Lowercase letters, numbers, hyphens. 3–50 chars. Must start with a letter."
            value={form.id}
            onChange={(e) => set('id', e.target.value)}
            placeholder="my-ecosystem"
          />
        </div>

        <div className="eco-admin-field">
          <label className="eco-admin-label" htmlFor="new-display">Display Name *</label>
          <input
            id="new-display"
            className="eco-admin-input"
            type="text"
            required
            maxLength={80}
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
            placeholder="My Ecosystem"
          />
        </div>

        <div className="eco-admin-field">
          <label className="eco-admin-label" htmlFor="new-group">Member Group</label>
          <input
            id="new-group"
            className="eco-admin-input"
            type="text"
            value={form.memberGroup}
            onChange={(e) => set('memberGroup', e.target.value)}
            placeholder="my-ecosystem-users"
          />
        </div>

        <div className="eco-admin-field">
          <label className="eco-admin-label" htmlFor="new-gitea">Gitea Org</label>
          <input
            id="new-gitea"
            className="eco-admin-input"
            type="text"
            value={form.giteaOrg}
            onChange={(e) => set('giteaOrg', e.target.value)}
            placeholder="my-ecosystem"
          />
        </div>

        <div className="eco-admin-field eco-admin-field-full">
          <label className="eco-admin-label" htmlFor="new-logo">Logo Path</label>
          <input
            id="new-logo"
            className="eco-admin-input"
            type="text"
            value={form.logo}
            onChange={(e) => set('logo', e.target.value)}
            placeholder="ecosystems/my-ecosystem/logo.svg"
          />
        </div>
      </div>

      <div className="eco-admin-section-label">Services</div>
      <ServiceCheckboxes
        selected={form.services}
        onChange={(services) => set('services', services)}
      />

      <div className="eco-admin-section-label">Palette</div>
      <PaletteEditor
        palette={form.palette}
        onChange={(palette) => set('palette', palette)}
      />

      <div className="eco-admin-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Creating…' : 'Create Ecosystem'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Edit Panel
// ---------------------------------------------------------------------------

interface EditFormState {
  displayName: string;
  logo: string;
  giteaOrg: string;
  services: string[];
  palette: Record<string, string>;
}

function EditEcosystemPanel({
  entry,
  onSaved,
  onCancel,
}: {
  entry: EcosystemAdminEntry;
  onSaved: (updated: EcosystemAdminEntry) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<EditFormState>({
    displayName: entry.displayName,
    logo: entry.logo,
    giteaOrg: entry.giteaOrg,
    services: entry.services,
    palette: { ...entry.palette },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof EditFormState>(key: K, value: EditFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      const updated = await adminUpdateEcosystem(token, entry.id, form);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="eco-admin-panel eco-admin-panel-edit" onSubmit={handleSubmit}>
      <h3 className="eco-admin-panel-title">Edit: {entry.id}</h3>

      {error && <div className="settings-message settings-error">{error}</div>}

      <div className="eco-admin-form-grid">
        <div className="eco-admin-field">
          <label className="eco-admin-label" htmlFor={`edit-display-${entry.id}`}>Display Name</label>
          <input
            id={`edit-display-${entry.id}`}
            className="eco-admin-input"
            type="text"
            required
            maxLength={80}
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
          />
        </div>

        <div className="eco-admin-field">
          <label className="eco-admin-label" htmlFor={`edit-gitea-${entry.id}`}>Gitea Org</label>
          <input
            id={`edit-gitea-${entry.id}`}
            className="eco-admin-input"
            type="text"
            value={form.giteaOrg}
            onChange={(e) => set('giteaOrg', e.target.value)}
          />
        </div>

        <div className="eco-admin-field eco-admin-field-full">
          <label className="eco-admin-label" htmlFor={`edit-logo-${entry.id}`}>Logo Path</label>
          <input
            id={`edit-logo-${entry.id}`}
            className="eco-admin-input"
            type="text"
            value={form.logo}
            onChange={(e) => set('logo', e.target.value)}
          />
        </div>
      </div>

      <div className="eco-admin-section-label">Services</div>
      <ServiceCheckboxes
        selected={form.services}
        onChange={(services) => set('services', services)}
      />

      <div className="eco-admin-section-label">Palette</div>
      <PaletteEditor
        palette={form.palette}
        onChange={(palette) => set('palette', palette)}
      />

      <div className="eco-admin-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Provision Commands Modal
// ---------------------------------------------------------------------------

function ProvisionCommandsModal({
  ecosystemId,
  onClose,
}: {
  ecosystemId: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const commands = buildProvisionCommands(ecosystemId);

  function handleCopy() {
    navigator.clipboard.writeText(commands).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="eco-admin-modal-backdrop" onClick={onClose}>
      <div className="eco-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="eco-admin-modal-header">
          <h3>Provisioning Commands — {ecosystemId}</h3>
          <button type="button" className="eco-admin-modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="eco-admin-modal-desc">
          Run these scripts from the <code>infrastructure/</code> directory on the host to finish provisioning.
        </p>
        <pre className="eco-admin-provision-pre">{commands}</pre>
        <div className="eco-admin-actions">
          <button type="button" className="btn btn-primary" onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Keycloak realm group members (ecosystem memberGroup)
// ---------------------------------------------------------------------------

function EcosystemGroupMembersPanel({ ecosystemId }: { ecosystemId: string }) {
  const [members, setMembers] = useState<EcosystemGroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      const data = await adminListEcosystemGroupMembers(token, ecosystemId);
      setMembers(data.members);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Intentionally reload only when ecosystem changes; `load` closes over latest state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecosystemId]);

  async function add(ev: FormEvent) {
    ev.preventDefault();
    setBusy(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      await adminAddEcosystemGroupMember(token, ecosystemId, email.trim());
      setEmail('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setBusy(false);
    }
  }

  async function removeMemberRow(m: EcosystemGroupMember) {
    setBusy(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      await adminRemoveEcosystemGroupMember(token, ecosystemId, {
        email: m.email || undefined,
        userId: m.email ? undefined : m.id,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="eco-admin-group-panel"
      style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>Keycloak group members</div>
      {loading ? (
        <p className="ecosystem-description">Loading…</p>
      ) : error ? (
        <p className="ecosystem-description" style={{ color: '#f87171' }}>{error}</p>
      ) : members.length === 0 ? (
        <p className="ecosystem-description">No members in this realm group.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem' }}>
          {members.map((m) => (
            <li
              key={m.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                marginBottom: '0.35rem',
              }}
            >
              <span>
                {m.name || m.username || m.id}
                {m.email ? <span style={{ opacity: 0.75 }}> ({m.email})</span> : null}
              </span>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void removeMemberRow(m)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="admin-member-form" onSubmit={add} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="email"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ minWidth: '200px' }}
        />
        <button type="submit" className="btn btn-primary" disabled={busy}>Add to group</button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ecosystem Row
// ---------------------------------------------------------------------------

function EcosystemRow({
  entry,
  onEdit,
  onDecommission,
  onReactivate,
  onShowProvision,
}: {
  entry: EcosystemAdminEntry;
  onEdit: () => void;
  onDecommission: () => void;
  onReactivate: () => void;
  onShowProvision: () => void;
}) {
  const isDecommissioned = entry.decommissioned === true;

  return (
    <div>
      <div className={`eco-admin-row ${isDecommissioned ? 'eco-admin-row-decommissioned' : ''}`}>
        <div className="eco-admin-row-main">
          <div className="eco-admin-row-id">
            <code>{entry.id}</code>
            {isDecommissioned && <span className="eco-admin-badge eco-admin-badge-dead">Decommissioned</span>}
            {!isDecommissioned && <span className="eco-admin-badge eco-admin-badge-active">Active</span>}
          </div>
          <div className="eco-admin-row-name">{entry.displayName}</div>
          <div className="eco-admin-row-meta">
            <span className="eco-admin-meta-item">Group: <code>{entry.memberGroup}</code></span>
            <span className="eco-admin-meta-item">Gitea: <code>{entry.giteaOrg}</code></span>
          </div>
          <div className="eco-admin-row-services">
            {entry.services.map((svc) => (
              <span key={svc} className="eco-admin-service-chip">{svc}</span>
            ))}
          </div>
        </div>
        <div className="eco-admin-row-actions">
          {!isDecommissioned && (
            <button type="button" className="btn btn-secondary" onClick={onEdit}>Edit</button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onShowProvision}>Scripts</button>
          {isDecommissioned
            ? <button type="button" className="btn btn-primary" onClick={onReactivate}>Reactivate</button>
            : <button type="button" className="btn btn-secondary eco-admin-btn-danger" onClick={onDecommission}>Decommission</button>
          }
        </div>
      </div>
      {entry.memberGroup ? <EcosystemGroupMembersPanel ecosystemId={entry.id} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function EcosystemAdminPage({ user }: EcosystemAdminPageProps) {
  const [ecosystems, setEcosystems] = useState<EcosystemAdminEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [pageSuccess, setPageSuccess] = useState('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [provisionId, setProvisionId] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    loadEcosystems();
  }, []);

  async function loadEcosystems() {
    setLoading(true);
    setPageError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      const list = await adminListEcosystems(token);
      setEcosystems(list);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load ecosystems');
    } finally {
      setLoading(false);
    }
  }

  function handleCreated(created: EcosystemAdminEntry) {
    setEcosystems((prev) => [...prev, created]);
    setShowAddForm(false);
    setPageSuccess(`Ecosystem "${created.id}" created. Run provisioning scripts to finish setup.`);
    setProvisionId(created.id);
    setTimeout(() => setPageSuccess(''), 6000);
  }

  function handleSaved(updated: EcosystemAdminEntry) {
    setEcosystems((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    setEditingId(null);
    setPageSuccess(`Ecosystem "${updated.id}" updated.`);
    setTimeout(() => setPageSuccess(''), 4000);
  }

  async function handleDecommission(id: string) {
    if (!window.confirm(`Decommission "${id}"? Existing members will lose access immediately.`)) return;
    setBusyId(id);
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      await adminDecommissionEcosystem(token, id);
      setEcosystems((prev) =>
        prev.map((e) => (e.id === id ? { ...e, decommissioned: true } : e))
      );
      setPageSuccess(`"${id}" decommissioned.`);
      setTimeout(() => setPageSuccess(''), 4000);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to decommission');
    } finally {
      setBusyId(null);
    }
  }

  async function handleReactivate(id: string) {
    setBusyId(id);
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('Not authenticated');
      await adminReactivateEcosystem(token, id);
      setEcosystems((prev) =>
        prev.map((e) => (e.id === id ? { ...e, decommissioned: false } : e))
      );
      setPageSuccess(`"${id}" reactivated.`);
      setTimeout(() => setPageSuccess(''), 4000);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to reactivate');
    } finally {
      setBusyId(null);
    }
  }

  const active = ecosystems.filter((e) => !e.decommissioned);
  const decommissioned = ecosystems.filter((e) => e.decommissioned);

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="navbar-brand">Patriotic Projects Admin</div>
        <div className="navbar-menu">
          <span className="dashboard-welcome">Signed in as {user.name || user.username}</span>
          <a href="/admin" className="btn btn-secondary">Admin Hub</a>
          <a href="/dashboard" className="btn btn-secondary">Dashboard</a>
        </div>
      </nav>

      <div className="container dashboard-container">
        <div className="dashboard-hero">
          <h1 className="dashboard-title">Ecosystem Management</h1>
          <p className="dashboard-subtitle">Create, edit, and decommission ecosystems in the branding registry.</p>
        </div>

        <div className="settings-message settings-error" style={{ marginBottom: '1rem' }} role="status">
          <strong>Legacy.</strong> Prefer{' '}
          <a href="/dashboard">Admin Hub</a> → <em>Ecosystem orchestration (Beta)</em> for new ecosystems
          via automated provisioning. This page remains for manual registry edits until orchestration is GA.
        </div>

        {pageError && <div className="settings-message settings-error">{pageError}</div>}
        {pageSuccess && <div className="settings-message settings-success">{pageSuccess}</div>}

        {/* Add ecosystem */}
        {!showAddForm ? (
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { setShowAddForm(true); setEditingId(null); }}
            >
              + Add Ecosystem
            </button>
          </div>
        ) : (
          <AddEcosystemForm
            onCreated={handleCreated}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {/* Ecosystem list */}
        {loading ? (
          <p className="ecosystem-description">Loading ecosystems…</p>
        ) : (
          <>
            <div className="ecosystem-card" style={{ padding: '1.5rem' }}>
              <h2 className="ecosystem-title" style={{ marginBottom: '1rem' }}>
                Active ({active.length})
              </h2>
              {active.length === 0 ? (
                <p className="ecosystem-description">No active ecosystems.</p>
              ) : (
                active.map((entry) => (
                  editingId === entry.id ? (
                    <EditEcosystemPanel
                      key={entry.id}
                      entry={entry}
                      onSaved={handleSaved}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <EcosystemRow
                      key={entry.id}
                      entry={entry}
                      onEdit={() => { setEditingId(entry.id); setShowAddForm(false); }}
                      onDecommission={() => { if (!busyId) handleDecommission(entry.id); }}
                      onReactivate={() => { if (!busyId) handleReactivate(entry.id); }}
                      onShowProvision={() => setProvisionId(entry.id)}
                    />
                  )
                ))
              )}
            </div>

            {decommissioned.length > 0 && (
              <div className="ecosystem-card" style={{ padding: '1.5rem', marginTop: '1.5rem', opacity: 0.75 }}>
                <h2 className="ecosystem-title" style={{ marginBottom: '1rem' }}>
                  Decommissioned ({decommissioned.length})
                </h2>
                {decommissioned.map((entry) => (
                  <EcosystemRow
                    key={entry.id}
                    entry={entry}
                    onEdit={() => {}}
                    onDecommission={() => {}}
                    onReactivate={() => { if (!busyId) handleReactivate(entry.id); }}
                    onShowProvision={() => setProvisionId(entry.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {provisionId && (
        <ProvisionCommandsModal
          ecosystemId={provisionId}
          onClose={() => setProvisionId(null)}
        />
      )}
    </div>
  );
}
